import type { DateTime } from 'luxon'

import type { SeriesTemplateAttributes, UserAttributes } from '@/server/models'

import { SQL, type SQLFragment } from '@/client/query'

import CourseQ from './CourseQ'
import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryLimitToSQL,
  queryOrderToSQL,
} from './common'
import SeriesHostQ from './SeriesHostQ'
import SeriesQ from './SeriesQ'
import SessionTemplateQ from './SessionTemplateQ'
import TopicQ from './TopicQ'
import TrainingQ from './TrainingQ'
import TrainingSubmissionQ from './TrainingSubmissionQ'
import UserQ from './UserQ'
import WorldQ from './WorldQ'

const SeriesTemplateQ = {
  ...makeQueryModelV2<SeriesTemplateAttributes>('series_template')([
    'id',
    'slug',
    'title',
    'external_description',
    'description',
    'curriculum',
    'curriculum_url',
    'attendance_policy',
    'schedule_type',
    'questionnaire',
    'max_learners',
    'allow_cohosts',
    'user_id',
    'course_id',
    'world_id',
    'active',
    'enabled',
    'gating_group',
    'minimum_start_date',
    'type',
    'max_registrations_per_learner',
    'max_active_series_per_tutor',
    'peer_review_tags',
    'peer_review_visibility',
    'tutor_training_id',
    'created_at',
    'updated_at',
    'deleted_at',
  ]),

  associations: (): AssociationDefinitions => ({
    World: {
      query: WorldQ,
      foreignKey: WorldQ.id,
      sourceKey: SeriesTemplateQ.world_id,
    },
    User: {
      query: UserQ,
      foreignKey: UserQ.id,
      sourceKey: SeriesTemplateQ.user_id,
    },
    Course: {
      query: CourseQ,
      foreignKey: CourseQ.id,
      sourceKey: SeriesTemplateQ.course_id,
    },
    SessionTemplates: {
      query: SessionTemplateQ,
      foreignKey: SessionTemplateQ.series_template_id,
      sourceKey: SeriesTemplateQ.id,
    },
    Training: {
      query: TrainingQ,
      foreignKey: TrainingQ.id,
      sourceKey: SeriesTemplateQ.tutor_training_id,
    },
  }),

  hasAnyCertificationRequiredTopics: (): SQLFragment<boolean> =>
    SQL`EXISTS(SELECT 1 ${SessionTemplateQ.where(
      {
        seriesTemplateId: SeriesTemplateQ.id,
        include: [
          {
            association: 'Topic',
            required: true,
          },
        ],
      },
      SQL`${TopicQ.requiresCertification()}`
    )})`,

  canUserTutorSessionTopics: ({
    userId,
  }: {
    userId: number | SQLFragment<number>
  }): SQLFragment<boolean> =>
    SQL`NOT EXISTS(SELECT 1 ${SessionTemplateQ.where(
      {
        seriesTemplateId: SeriesTemplateQ.id,
        include: [
          {
            association: 'Topic',
            required: true,
          },
        ],
      },
      SQL`(NOT (${TopicQ.canUserTutor(userId)}))`
    )})`,

  hasUserCompletedRequiredTraining: ({
    userId,
  }: {
    userId: number | SQLFragment<number>
  }): SQLFragment<boolean> =>
    SQL`(
      ${SeriesTemplateQ.tutor_training_id} IS NULL
      OR EXISTS(SELECT 1 ${TrainingSubmissionQ.where({
        userId,
        trainingId: SeriesTemplateQ.tutor_training_id,
        hasSubmitted: true,
      })})
    )`,

  canUserTutor: ({ userId }: { userId: number | SQLFragment<number> }): SQLFragment<boolean> =>
    SQL`
      ${SeriesTemplateQ.canUserTutorSessionTopics({ userId })}
      AND ${SeriesTemplateQ.hasUserCompletedRequiredTraining({ userId })}
    `,

  getUser: (): SQLFragment<UserAttributes> =>
    UserQ.where({
      id: SeriesTemplateQ.user_id,
    }),

  countSessions: (): SQLFragment<number> =>
    SQL`COUNT(*) ${SessionTemplateQ.where({ seriesTemplateId: SeriesTemplateQ.id })}`,

  countActiveSeries: (): SQLFragment<number> =>
    SQL`COUNT(*) ${SeriesQ.where(
      {
        seriesTemplateId: SeriesTemplateQ.id,
      },
      SQL`NOT ${SeriesQ.isClosed()}`
    )}`,

  countActiveSeriesByUser: ({
    userId,
  }: {
    userId: number | SQLFragment<number>
  }): SQLFragment<number> =>
    SQL`COUNT(*) ${SeriesHostQ.where({
      userId,
      approved: true,
      include: [
        {
          association: 'Series',
          required: true,
          where: SQL`NOT ${SeriesQ.hasEnded()} AND ${SeriesQ.series_template_id} = ${SeriesTemplateQ.id}`,
        },
      ],
    })}`,

  countActiveSeriesForWeek: ({
    weekStartDate,
  }: {
    weekStartDate: DateTime
  }): SQLFragment<number> => {
    const startISO = weekStartDate.startOf('day').toISODate() ?? ''
    const endISO = weekStartDate.startOf('day').plus({ weeks: 1 }).toISODate() ?? ''
    return SQL`COUNT(*) ${SeriesQ.where(
      {
        seriesTemplateId: SeriesTemplateQ.id,
      },
      SQL`NOT ${SeriesQ.isClosed()}
        AND (SELECT ${SeriesQ.getComputedStartDate()}) >= ${startISO}
        AND (SELECT ${SeriesQ.getComputedStartDate()}) < ${endISO}`
    )}`
  },

  isUserBookedForWeek: ({
    userId,
    weekStartDate,
  }: {
    userId: number | SQLFragment<number>
    weekStartDate: DateTime
  }): SQLFragment<boolean> => {
    const startISO = weekStartDate.startOf('day').toISODate() ?? ''
    const endISO = weekStartDate.startOf('day').plus({ weeks: 1 }).toISODate() ?? ''
    return SQL`EXISTS(SELECT 1 ${SeriesHostQ.where({
      userId,
      approved: true,
      include: [
        {
          association: 'Series',
          required: true,
          where: SQL`NOT ${SeriesQ.isClosed()}
            AND ${SeriesQ.series_template_id} = ${SeriesTemplateQ.id}
            AND (SELECT ${SeriesQ.getComputedStartDate()}) >= ${startISO}
            AND (SELECT ${SeriesQ.getComputedStartDate()}) < ${endISO}`,
        },
      ],
    })})`
  },

  where: (
    {
      paranoid = true,
      id,
      slug,
      userId,
      worldId,
      active,
      enabled,
      include = [],
      groupBy = null,
      order = null,
      limit = null,
    }: {
      paranoid?: boolean
      id?: number | null | SQLFragment<number>
      slug?: string | SQLFragment<string> | null
      userId?: number | null | SQLFragment<number>
      worldId?: number | null | SQLFragment<number>
      active?: boolean | null
      enabled?: boolean | null
      include?: IncludeAssociations
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      limit?: number | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<SeriesTemplateAttributes> => SQL`
        FROM ${SeriesTemplateQ}
        ${includeAssociationsToSql(SeriesTemplateQ, include)}
        WHERE
            ${id != null ? SQL`${SeriesTemplateQ.id} = ${id} AND` : SQL``}
            ${slug != null ? SQL`${SeriesTemplateQ.slug} = ${slug} AND` : SQL``}
            ${userId != null ? SQL`${SeriesTemplateQ.user_id} = ${userId} AND` : SQL``}
            ${worldId != null ? SQL`${SeriesTemplateQ.world_id} = ${worldId} AND` : SQL``}
            ${active != null ? SQL`${SeriesTemplateQ.active} = ${active} AND` : SQL``}
            ${enabled != null ? SQL`${SeriesTemplateQ.enabled} = ${enabled} AND` : SQL``}
            ${paranoid ? SQL`${SeriesTemplateQ.deleted_at} IS NULL AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
        ${queryLimitToSQL(limit)}
    `,
}

export default SeriesTemplateQ
